import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL;
const VITE_KNOKO_BEARER_TOKEN = import.meta.env.VITE_KNOKO_BEARER_TOKEN;

// Create axios instance with base URL
const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use(async (config) => {
    const knokoBearerToken = VITE_KNOKO_BEARER_TOKEN;
    if (knokoBearerToken) {
        config.headers.Authorization = `Bearer ${knokoBearerToken}`;
    }
    return config;
});

export const googleCalendarService = {
    /**
     * Get user's Google Calendar credentials/tokens from the database
     */
    getUserCredentials: async (uid,  googleCalendarUrl) => {
        try {
            const payload = {
                uid: uid,
                action: 'getUserCredentials'
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error fetching Google Calendar credentials:', error);
            throw error;
        }
    },

    /**
     * Initialize OAuth flow - get authorization URL
     */
    getAuthUrl: async (uid, googleCalendarUrl, redirectUri) => {
        try {
            const payload = {
                uid: uid,
                action: 'getAuthUrl',
                redirectUri: redirectUri
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error getting Google auth URL:', error);
            throw error;
        }
    },

    /**
     * Exchange authorization code for tokens
     */
    exchangeCodeForTokens: async (uid, code, googleCalendarUrl, redirectUri) => {
        try {
            const payload = {
                uid: uid,
                action: 'exchangeCode',
                code: code,
                redirectUri: redirectUri
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error exchanging code for tokens:', error);
            throw error;
        }
    },

    /**
     * Get list of user's calendars using GET request
     */
    getCalendarList: async (uid, googleCalendarUrl) => {
        try {
            const response = await apiClient.get(googleCalendarUrl, {
                params: {
                    type: 'getCalendars',
                    uid: uid
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching calendar list:', error);
            throw error;
        }
    },

    /**
     * Get calendar events using GET request
     */
    getCalendarEvents: async (uid, googleCalendarUrl, calendarId, startDate, endDate) => {
        try {
            const response = await apiClient.get(googleCalendarUrl, {
                params: {
                    type: 'getEvents',
                    uid: uid,
                    calendarId: calendarId,
                    startDate: startDate?.toISOString(),
                    endDate: endDate?.toISOString()
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            throw error;
        }
    },

    /**
     * Sync Google Calendar events
     */
    syncEvents: async (uid, googleCalendarUrl, calendars, startDate, endDate) => {
        try {
            const payload = {
                uid: uid,
                action: 'syncEvents',
                calendars: calendars,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString()
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error syncing Google Calendar events:', error);
            throw error;
        }
    },

    /**
     * Disconnect and remove Google Calendar integration
     */
    disconnect: async (uid, googleCalendarUrl) => {
        try {
            const payload = {
                uid: uid,
                action: 'disconnect'
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error disconnecting Google Calendar:', error);
            throw error;
        }
    },

    /**
     * Refresh access token using refresh token
     */
    refreshToken: async (uid, googleCalendarUrl) => {
        try {
            const payload = {
                uid: uid,
                action: 'refreshToken'
            };

            const response = await apiClient.post(googleCalendarUrl, payload);
            return response.data;
        } catch (error) {
            console.error('Error refreshing token:', error);
            throw error;
        }
    }
};
